import React, { useState, useEffect } from 'react';
import { Smartphone, X, Download } from 'lucide-react';

const STORAGE_KEY = 'storeops:android_install_dismissed_v1';

/**
 * Android/Chrome PWA install banner.
 *
 * Chrome (Android e desktop), Edge e Opera emettono l'evento
 * `beforeinstallprompt` quando l'app è eleggibile per l'installazione
 * (manifest + service worker + visite recurrenti). Intercettiamo
 * l'evento, lo conserviamo e mostriamo un nostro banner controllabile
 * invece di affidarci al mini-infobar nativo (che molti utenti ignorano).
 *
 * Mostrato solo quando:
 *   - Il browser supporta `beforeinstallprompt` (Chrome/Edge/Samsung Internet);
 *   - L'app NON è già in modalità standalone (già installata);
 *   - L'utente non ha già dismesso il banner in questo browser.
 *
 * Su iOS Safari non scatta — quella piattaforma usa `IOSInstallBanner.js`.
 */
export default function AndroidInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (window.localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch { /* private mode → continua */ }

    // Già installata? Non mostrare nulla.
    const isStandalone =
      window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) return;

    const handler = (e) => {
      // Blocca il mini-infobar nativo: vogliamo gestire noi il timing.
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      // Cosi al prossimo accesso (se l'app viene disinstallata) ricompare.
      try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch { /* */ }
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // outcome: "accepted" | "dismissed"
      if (outcome === 'accepted') {
        setVisible(false);
      }
      // L'evento è single-use: dopo prompt() va invalidato.
      setDeferredPrompt(null);
    } catch {
      // Non bloccante.
    } finally {
      setInstalling(false);
    }
  };

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch { /* */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900 text-white px-4 py-3 app-safe-bottom shadow-[0_-4px_20px_rgba(0,0,0,0.15)] flex items-start gap-3"
      data-testid="android-install-banner"
      role="dialog"
      aria-label="Installa StoreOps come app"
    >
      <div className="w-9 h-9 bg-white text-slate-900 flex items-center justify-center flex-shrink-0">
        <Smartphone className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">Installa StoreOps</p>
        <p className="text-[12px] text-slate-300 leading-snug mt-1">
          Aggiungilo alla schermata Home per accedere più velocemente a ferie, profilo e ticket.
        </p>
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            onClick={install}
            disabled={installing}
            className="bg-white text-slate-900 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-slate-100 transition-colors disabled:opacity-50"
            data-testid="android-install-banner-install"
          >
            <Download className="w-3.5 h-3.5" />
            {installing ? 'Installazione…' : 'Installa app'}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="text-xs text-slate-300 hover:text-white transition-colors px-2 py-1.5"
            data-testid="android-install-banner-later"
          >
            Più tardi
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="p-1 -mr-1 -mt-1 text-slate-400 hover:text-white transition-colors flex-shrink-0"
        data-testid="android-install-banner-dismiss"
        aria-label="Chiudi"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
